class SylvieCarousel extends HTMLElement {
	async connectedCallback() {
		this.track = this.querySelector('ul');
		if (!this.track) return;

		// The PDP opts into native scroll snap. Avoiding Embla here removes nested
		// scrollers and transformed loop slides that WebKit can size or paint
		// incorrectly. Other carousel consumers continue to use Embla.
		if (this.dataset.mobileOnly === 'true') return;

		await this.initEmbla();
	}

	async initEmbla() {
		if (this.embla) return;
		const { default: EmblaCarousel } = await import('https://cdn.jsdelivr.net/npm/embla-carousel/+esm');

		// Embla's contract: the root you hand it must have exactly one child
		// (the "container" it transforms), whose children are the slides. Our
		// track (<ul>) IS that container — its <li>s are the slides — so the
		// root Embla needs is the track's parent, not the track itself. Passing
		// the track directly made Embla treat the first <li> as the container
		// and that card's own children as "slides", so only the first item ever
		// moved and every other slide stayed static.
		this.viewport = this.track.parentElement;

		// data-slides-to-scroll[-desktop]: how many slides one dot/scroll step
		// advances (e.g. product-recommendations scrolls 2 at a time on mobile,
		// 4 at 1024px+, matching the app's Carousel breakpoints option). Read
		// once at init — not re-evaluated on resize, same simplification as
		// the rest of this component.
		const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
		const slidesToScroll = Number(
			(isDesktop && this.dataset.slidesToScrollDesktop) || this.dataset.slidesToScroll || 1
		);

		this.embla = EmblaCarousel(this.viewport, {
			loop: this.dataset.loop === 'true',
			align: 'center',
			containScroll: 'trimSnaps',
			skipSnaps: true,
			slidesToScroll,
		});

		// Embla now owns horizontal movement. Hide the viewport's native overflow
		// so the CSS `overflow-x: auto` no-JS fallback stops double-handling
		// touch input alongside Embla once JS is active.
		this.viewport.style.overflow = 'hidden';

		// Let listeners outside the carousel (e.g. dot indicators) track the
		// active slide without reaching into the Embla instance directly.
		this.emit = () => this.dispatchEvent(new CustomEvent('carousel:select', {
			bubbles: true, detail: { index: this.embla.selectedScrollSnap() }
		}));
		this.embla.on('select', this.emit);
		this.embla.on('init', this.emit);
		this.buildDots();
		this.emit(); // ensure an initial fire even if 'init' already passed
	}

	// Opt-in: a `[data-carousel-dots]` element anywhere inside the host gets
	// one button per Embla scroll snap (a "page", not a slide — matches the
	// app's Carousel navigation snippet), wired to scrollTo + active state.
	buildDots() {
		this.dotsEl = this.querySelector('[data-carousel-dots]');
		if (!this.dotsEl) return;
		const snaps = this.embla.scrollSnapList();
		this.dotsEl.hidden = snaps.length <= 1;
		this.dotsEl.replaceChildren();
		this.dotButtons = snaps.map((_, i) => {
			const li = document.createElement('li');
			const btn = document.createElement('button');
			btn.type = 'button';
			if (this.dotsEl.dataset.dotClass) btn.className = this.dotsEl.dataset.dotClass;
			btn.dataset.dot = String(i);
			btn.setAttribute('aria-label', `Scroll to slide ${i + 1}`);
			btn.addEventListener('click', () => this.embla.scrollTo(i));
			li.appendChild(btn);
			this.dotsEl.appendChild(li);
			return btn;
		});
		this.addEventListener('carousel:select', (e) => {
			this.dotButtons.forEach((btn, i) => btn.classList.toggle('is-active', i === e.detail.index));
		});
	}

	destroyEmbla() {
		this.embla?.destroy();
		this.embla = null;
		if (this.viewport) this.viewport.style.overflow = '';
	}

	disconnectedCallback() {
		this.mql?.removeEventListener('change', this.onBreakpointChange);
		this.destroyEmbla();
	}
}
customElements.define('sylvie-carousel', SylvieCarousel);
